import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { auth, db } from "../firebase";
import { DailyScanStat, QRCode, ScanEvent } from "../types";

const requireAuth = () => {
  if (!auth.currentUser) throw new Error("Du musst angemeldet sein.");
};

export class AnalyticsService {
  static async getDailyStats(qrId: string): Promise<DailyScanStat[]> {
    requireAuth();
    const snapshot = await getDocs(
      query(
        collection(db, "qr-codes", qrId, "daily-stats"),
        orderBy("date", "desc"),
        limit(730),
      ),
    );
    return snapshot.docs
      .map((item) => ({
        date: item.data().date || item.id,
        scans: item.data().scans || 0,
        uniqueVisitors: item.data().uniqueVisitors || 0,
      }))
      .reverse();
  }

  static async getRecentEvents(
    qrId: string,
    eventLimit = 1000,
  ): Promise<ScanEvent[]> {
    requireAuth();
    const snapshot = await getDocs(
      query(
        collection(db, "qr-codes", qrId, "scan-events"),
        orderBy("scannedAt", "desc"),
        limit(eventLimit),
      ),
    );
    return snapshot.docs.map((item) => ({
      id: item.id,
      qrId,
      scannedAt: item.data().scannedAt?.toDate?.() || new Date(),
      destination: item.data().destination || "",
      ruleId: item.data().ruleId,
      device: item.data().device,
      browser: item.data().browser,
      os: item.data().os,
      country: item.data().country,
      language: item.data().language,
      referrer: item.data().referrer,
      visitorHash: item.data().visitorHash,
      isRepeat: Boolean(item.data().isRepeat),
    }));
  }

  static exportEventsCSV(
    events: ScanEvent[],
    filename: string,
    qrCode?: QRCode,
    daily: DailyScanStat[] = [],
  ): void {
    const headers = [
      "Datensatz",
      "Anzahl",
      "Zeitpunkt",
      "QR-ID",
      "Ziel",
      "Neu/Wiederholt",
      "Gerät",
      "Browser",
      "Betriebssystem",
      "Land",
      "Sprache",
      "Referrer",
    ];
    const escape = (value: unknown) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;
    const trackedTotal = daily.reduce((sum, item) => sum + item.scans, 0);
    const historicalScans = qrCode
      ? Math.max(0, qrCode.accessCount - trackedTotal)
      : 0;
    const summaryRows = qrCode
      ? [
          [
            "Zusammenfassung",
            qrCode.accessCount,
            "",
            qrCode.id,
            qrCode.target,
            `${trackedTotal} detailliert / ${historicalScans} historisch`,
            "",
            "",
            "",
            "",
            "",
            "",
          ]
            .map(escape)
            .join(","),
        ]
      : [];
    const rows = events.map((event) =>
      [
        "Scan",
        1,
        event.scannedAt.toISOString(),
        event.qrId,
        event.destination,
        event.isRepeat ? "Wiederholt" : "Neu",
        event.device,
        event.browser,
        event.os,
        event.country,
        event.language,
        event.referrer,
      ]
        .map(escape)
        .join(","),
    );
    const blob = new Blob(
      [`\uFEFF${headers.join(",")}\n${[...summaryRows, ...rows].join("\n")}`],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  static async exportPDFReport(
    qrCode: QRCode,
    daily: DailyScanStat[],
    events: ScanEvent[],
  ): Promise<void> {
    const { jsPDF } = await import("jspdf");
    const report = new jsPDF();
    const trackedTotal = daily.reduce((sum, item) => sum + item.scans, 0);
    const historicalScans = Math.max(0, qrCode.accessCount - trackedTotal);
    const unique = daily.reduce((sum, item) => sum + item.uniqueVisitors, 0);
    const recent = daily.slice(-14);
    const lines = [
      `QR-Code: ${qrCode.title || qrCode.id}`,
      `ID: ${qrCode.id}`,
      `Ziel: ${qrCode.target}`,
      `Erstellt: ${new Intl.DateTimeFormat("de-DE").format(qrCode.createdAt)}`,
      `Gesamte Scans: ${qrCode.accessCount}`,
      `Detailliert erfasste Scans: ${trackedTotal}`,
      `Historische Scans ohne Detaildaten: ${historicalScans}`,
      `Eindeutige Tagesbesucher: ${unique}`,
      `Letzte erfasste Events: ${events.length}`,
      `Status: ${qrCode.health?.status || "nicht geprüft"}`,
    ];
    report.setFontSize(20);
    report.text("RedirectQR Analytics-Bericht", 16, 20);
    report.setFontSize(10);
    report.text(
      `Erstellt am ${new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date())}`,
      16,
      28,
    );
    report.setFontSize(11);
    report.text(lines, 16, 42, { maxWidth: 178 });
    let y = 88;
    report.setFontSize(13);
    report.text("Letzte 14 Tage", 16, y);
    y += 8;
    report.setFontSize(10);
    recent.forEach((item) => {
      if (y > 280) {
        report.addPage();
        y = 20;
      }
      report.text(
        `${item.date}: ${item.scans} Scans, ${item.uniqueVisitors} Besucher`,
        16,
        y,
      );
      y += 6;
    });
    report.save(`${qrCode.id}-analytics.pdf`);
  }
}
